import { Injectable, Inject, inject } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Environment } from '../../environment/environment.model';
import { DiarizationResponse } from '../_models/diarization-response.model';
import { Observable } from 'rxjs';
import { ProductSuggestionRequest } from '../_models/product-suggestion-req.model';
import { ProductSuggestionResponse } from '../_models/product-suggestion-res.model';

@Injectable({
  providedIn: 'root',
})
export class TaskService {
  constructor(@Inject('env') private environment: Environment) { }
  private httpClient = inject(HttpClient);

  // The conversation report as .docx, filled by Mynte from the transcript. With a client
  // the backend fills the Suites fields (client, work coach, ...) in afterwards.
  public summarizeTranscript(
    transcriptDocument: Blob,
    conversationType: string,
    clientNummer: number | null = null,
    werkprocesNummer: number | null = null,
  ): Observable<Blob> {
    const formData = new FormData();
    formData.append('Transcript', transcriptDocument, 'transcript.docx');
    formData.append('ConversationType', conversationType);
    this.appendSuitesNumbers(formData, clientNummer, werkprocesNummer);

    return this.httpClient.post(`${this.environment.apiUrl}/mynte/summarize-transcript`, formData, {
      responseType: 'blob',
    });
  }

  public summarizeWerkprocesFiles(werkprocesNummer: number): Observable<HttpResponse<Blob>> {
    return this.httpClient.post(
      `${this.environment.apiUrl}/mynte/summarize-files/${werkprocesNummer}`,
      null,
      { responseType: 'blob', observe: 'response' },
    );
  }

  public summarizeClientFiles(clientNummer: number): Observable<HttpResponse<Blob>> {
    return this.httpClient.post(
      `${this.environment.apiUrl}/mynte/summarize-files/client/${clientNummer}`,
      null,
      { responseType: 'blob', observe: 'response' },
    );
  }

  public verifyActionPlan(
    actionPlanFile: Blob,
    documents: (File | Blob)[] = [],
  ): Observable<{ reden: string; beoordeling: string; onjuistheden: string[] }> {
    const formData = new FormData();
    formData.append(
      'ActionPlan',
      new File([actionPlanFile], 'action-plan.docx', {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      }),
    );
    documents.forEach((fileOrBlob, idx) => {
      const name = (fileOrBlob as File).name || `document-${idx + 1}.docx`;
      formData.append('Documents', fileOrBlob, name);
    });
    return this.httpClient.post<{ reden: string; beoordeling: string; onjuistheden: string[] }>(
      `${this.environment.apiUrl}/mynte/verify-action-plan`,
      formData,
    );
  }

  public createActionPlan(
    documentType: string,
    transcriptDocument: Blob,
    documents: (File | Blob)[] = [],
    clientNummer: number | null = null,
    werkprocesNummer: number | null = null,
  ): Observable<Blob> {
    const formData = new FormData();
    formData.append(
      'Transcript',
      new File([transcriptDocument], 'transcript.docx', {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      }),
    );
    formData.append('TemplateType', documentType);
    documents.forEach((fileOrBlob, idx) => {
      const name = (fileOrBlob as File).name || `document-${idx + 1}.docx`;
      formData.append('Documents', fileOrBlob, name);
    });
    this.appendSuitesNumbers(formData, clientNummer, werkprocesNummer);
    return this.httpClient.post(`${this.environment.apiUrl}/mynte/fill-placeholders`, formData, {
      responseType: 'blob',
    });
  }

  // The decision as .docx, filled from the decision template the user chose.
  public createDecision(
    templateType: string,
    products: string[],
    actionPlanFile: Blob,
    documents: (File | Blob)[] = [],
    clientNummer: number | null = null,
    werkprocesNummer: number | null = null,
  ): Observable<Blob> {
    const formData = new FormData();

    formData.append('TemplateType', templateType);
    formData.append(
      'ActionPlan',
      new File([actionPlanFile], 'action-plan.docx', {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      }),
    );

    products.forEach((product) => {
      formData.append('Products', product);
    });
    documents.forEach((fileOrBlob, idx) => {
      const name = (fileOrBlob as File).name || `document-${idx + 1}.docx`;
      formData.append('Documents', fileOrBlob, name);
    });
    this.appendSuitesNumbers(formData, clientNummer, werkprocesNummer);

    return this.httpClient.post(`${this.environment.apiUrl}/mynte/create-decision`, formData, {
      responseType: 'blob',
    });
  }

  public checkDecision(
    decisionFile: File,
    documents: (File | Blob)[] = [],
  ): Observable<{ reason: string; verdict: string; firstLineOfDocument: string }> {
    const formData = new FormData();

    formData.append('Decision', decisionFile);
    documents.forEach((fileOrBlob, idx) => {
      const name = (fileOrBlob as File).name || `document-${idx + 1}.docx`;
      formData.append('Documents', fileOrBlob, name);
    });

    return this.httpClient.post<{ reason: string; verdict: string; firstLineOfDocument: string }>(
      `${this.environment.apiUrl}/mynte/check-decision`,
      formData,
    );
  }

  public productSuggestion(
    data: ProductSuggestionRequest & { documents?: Blob[] },
  ): Observable<ProductSuggestionResponse> {
    const formData = new FormData();
    formData.append('ActionPlan', data.actionPlan);
    data.products.forEach((product) => {
      formData.append('Products', product);
    });
    (data.documents || []).forEach((fileOrBlob, idx) => {
      const name = (fileOrBlob as File).name || `document-${idx + 1}.docx`;
      formData.append('Documents', fileOrBlob, name);
    });
    return this.httpClient.post<ProductSuggestionResponse>(
      `${this.environment.apiUrl}/mynte/suggest-product`,
      formData,
    );
  }

  // The backend looks the client up in Suites and fills those fields after Mynte.
  private appendSuitesNumbers(
    formData: FormData,
    clientNummer: number | null | undefined,
    werkprocesNummer: number | null | undefined,
  ): void {
    if (clientNummer != null) {
      formData.append('ClientNummer', clientNummer.toString());
    }
    if (werkprocesNummer != null) {
      formData.append('WerkprocesNummer', werkprocesNummer.toString());
    }
  }
}
